import { Draft } from 'immer';

import { finleyAssert } from '@finley-technologies/common';
import { CreditAgreement } from '@finley-technologies/finley-types';

import { BasePricingOptionFormData } from '~/core/routes/settings/implementations/[implementationId]/facilities/[facilityId]/pricing-options/types';

type CreditAgreementFullArtifact = CreditAgreement.DomainEntity.CreditAgreementFullArtifact;

// This function receives a 'draft' of the Credit Agreement, which is provided by the immer library.
// This means that it is ok to mutate the `draft` variable - this produces a new CA object, and leaves
// the original CA reference untouched.
export const setFormValues = (
  draft: Draft<CreditAgreementFullArtifact>,
  formValues: BasePricingOptionFormData,
) => {
  // It shouldn't be possible for loanMechanics to be undefined at this point
  // but this helps with types.
  draft.loanMechanicsV1 = draft.loanMechanicsV1 || {
    pricingOptions: [],
    loans: [],
    commitments: [],
  };

  const pricingOption = draft?.loanMechanicsV1?.pricingOptions?.find(
    (pricingOptionItem) => pricingOptionItem.key === formValues.key,
  );

  finleyAssert(
    pricingOption,
    `Pricing option [id: ${formValues.key}] not found in loanMechanicsV1.pricingOptions array [pricing options array length: ${draft?.loanMechanicsV1?.pricingOptions?.length}]`,
  );

  // required fields: name of rate must be set
  if (!formValues.name) {
    return;
  }

  // general pricing option properties
  pricingOption.key = formValues.key;
  pricingOption.name = formValues.name;

  if (formValues.type === 'fixed') {
    finleyAssert(
      pricingOption.type === 'fixed',
      `Pricing option [id: ${formValues.key}] was expecting a 'fixed' rateType, but was [rateType: ${pricingOption.type}]`,
    );
    pricingOption.value = formValues.value ?? 0;
  } else if (formValues.type === 'benchmark') {
    finleyAssert(
      pricingOption.type === 'benchmark',
      `Pricing option [id: ${formValues.key}] was expecting a 'benchmark' rateType, but was [rateType: ${pricingOption.type}]`,
    );
    pricingOption.margin = {
      type: 'fixed' as const,
      value: formValues.margin ?? 0,
    };
    pricingOption.adjustments = [];
    // For MVP, 'adjustment' is the aggregated values of all spreads in the adjustments array. Sachin will be updating adjustments array in loan mechanicsV2, so users can configure 1+ spreads.
    if (formValues.spread !== null) {
      pricingOption.adjustments.push({
        type: 'adjustment',
        creditAdjustmentSpread: true,
        value: formValues.spread,
      });
    }
    if (formValues.floor !== null) {
      pricingOption.adjustments.push({
        type: 'floor',
        value: formValues.floor,
      });
    }
    if (formValues.ceiling !== null) {
      pricingOption.adjustments.push({
        type: 'ceiling',
        value: formValues.ceiling,
      });
    }
    pricingOption.determination = formValues.determination;

    /** DATE CONFIG */
    const relativeRecurringDateConfig =
      formValues.relativeRecurringDateConfig?.dateConfig
        ?.relativeRecurringDateConfig;
    if (
      formValues.changeType === 'recurring' &&
      relativeRecurringDateConfig &&
      relativeRecurringDateConfig.length > 0
    ) {
      // todo: waiting for exclusions and mapping all relative date configs - @sachinr4
      pricingOption.change = {
        type: 'recurring',
        dateConfig: relativeRecurringDateConfig[0].baseDateConfig,
      };
    } else if (formValues.changeType === 'daily') {
      pricingOption.change = {
        type: 'daily',
      };
    }
  }
};
